export interface GetCardScanSecretRequest {
  checkpoint: string
  traceId: string
}

export interface GetCardScanSecretResponse {
  id: string // cvi_id
  secret: string // cvi_secret
}

export interface GetCardScanResultRequest {
  civ: {
    id: string
  }
  traceId: string
  checkpoint: string
  externalId: string
}

export interface ScanResult {
  readonly canceled: boolean
  readonly cannotScan: boolean
  readonly stopped: boolean
  readonly completed: boolean
  readonly cardImageVerificationId: string
}

export interface ScanResultV2 {
  readonly data: {
    scanId: string
    status: string
    request: {
      verify: {
        cardholderName: string
        firstSix: string
        lastFour: string
        detectWrongCard: boolean
      }
    }
    options: {
      logoUrl: string
      userId: string
    }
    isFraud: boolean
    declineReasons: {
      formatMismatch: boolean
      numberMismatch: boolean
      generatedImage: boolean
      rateLimited: boolean
    }
    failureReason: string
    scanResult: {
      firstSix: string
      lastFour: string
      expirationDate: string
    }
  }
  readonly completed: boolean
}

export interface VerifyScanResult {
  isMatched: boolean
  isRetryable: boolean
}

export enum CheckPoint {
  CheckoutConfirm = 'CHECKOUT_CONFIRM',
  AddPayment = 'ADD_PAYMENT_WEB',
}

export interface IScanConfiguration {
  localization?: {
    instructionsLoading?: string
    instructionsReading?: string
    instructionsScan?: string
    instructionsCapturing?: string
    instructionsProcessing?: string
    instructionsWrongCard?: string
    instructionsTroubleScanning?: string
    securityNotification?: string
  }
  css?: string
}

export interface ScanIntent {
  readonly isAvailable: () => Promise<boolean>
  readonly configureScan: (config: IScanConfiguration) => void
  readonly startScan: () => Promise<ScanResult>
  readonly stopScan: () => void
  readonly iframe: HTMLIFrameElement
}

export interface ScanIntentV2 {
  readonly present: (userId: string, config: {}) => Promise<ScanResultV2>
}

export enum BrowserType {
  Others = 'others',
  Chrome = 'chrome',
  Safari = 'safari',
  Samsung = 'samsung',
}

export enum ScanStatus {
  Initial,
  Creating,
  Ready,
  Scanning,
  Finished,
}

export enum CardScanError {
  Unsupported = 'unsupported',
  DidntMatch = 'didnt_match',
  CannotScan = 'cannot_scan',
  TooManyTries = 'too_many_tries',
  NoPermission = 'no_permission',
  UserCanceled = 'user_canceled',
  TerminalError = 'terminal_error',
}

export enum CardScanAmplitudeErrorReason {
  NotMatched = 'Not Matched',
  RateLimit = 'Rate Limit',
  OtherReasons = 'Other Reasons',
}

export enum DyScanFailureReason {
  NoWebcam = 'noWebcam',
  TimedOut = 'timedOut',
}

export const isScanIntent = (scanIntent: any): scanIntent is ScanIntent => {
  return scanIntent !== null && typeof scanIntent !== 'undefined' && 'iframe' in scanIntent
}

export const isScanIntentV2 = (scanIntentV2: any): scanIntentV2 is ScanIntentV2 => {
  return scanIntentV2 !== null && typeof scanIntentV2 !== 'undefined'
}

type CardScanNoPermissionError = 'Permission denied' | 'Permission denied by system'
export const isNoPermissionError = (message: string): message is CardScanNoPermissionError => {
  return ['Permission denied', 'Permission denied by system'].includes(message)
}

export const getCheckpoint = (message?: string): CheckPoint => {
  return message?.toLowerCase() === 'add_payment' ? CheckPoint.AddPayment : CheckPoint.CheckoutConfirm
}
